import { Link } from "react-router";
import MainLayout from "@/components/layouts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { getInitials } from "@/lib/utils.ts";
import useClients from "./clients.hooks";
import { useAdministersActiveOrganization } from "@/hooks/useAdministersActiveOrganization";
import {
  DropdownMenuContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, UserPlus } from "lucide-react";
import { Customer, CustomerSortKey } from "@/repositories/client.repository";
import { EditClientContact } from "./clients.components/edit-contact-modal";
import { InviteClientModal } from "./clients.components/invite-client-modal";

export default function ClientsPage() {
  const {
    list,
    loading,
    total,
    page,
    pageSize,
    name,
    setPage,
    setPageSize,
    setName,
    sortKey,
    sortOrder,
    setSortKey,
    editingModalOpen,
    setEditingModalOpen,
    onSubmit,
    getClientContact,
    clientToEdit,
    inviteModalOpen,
    setInviteModalOpen,
    inviteSaving,
    inviteError,
    handleInviteClient,
  } = useClients();
  const administersVendor = useAdministersActiveOrganization();

  const columns: ColumnDef<Customer>[] = [
    {
      header: "Name",
      accessor: (row) => (
        <Link to={`/clients/${row.id}`} className="hover:underline flex items-center">
          <Avatar className="w-8 h-8 mr-4">
            <AvatarImage src={row.image ?? undefined} />
            <AvatarFallback>{getInitials(row.name)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span>{row.name}</span>
            <span className="text-xs text-muted-foreground">{row.email}</span>
          </div>
        </Link>
      ),
      sortable: true,
      sortKey: "name",
    },
    {
      header: "Contact Person",
      accessor: (row) => {
        if (!row.contact) return "-";
        return (
          <div className="flex items-center">
            <div className="flex flex-col">
              <span>{row.contact.name}</span>
              <span className="text-xs text-muted-foreground">{row.contact.email}</span>
            </div>
          </div>
        );
      },
      sortable: true,
      sortKey: "email",
    },
    {
      header: "Actions",
      accessor: (row) => (
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Link to={`/clients/${row.id}`}>
                <DropdownMenuItem>View Details</DropdownMenuItem>
              </Link>
              {administersVendor && (
                <DropdownMenuItem
                  onSelect={() => {
                    setTimeout(() => {
                      getClientContact(row.id);
                      setEditingModalOpen(true);
                    }, 0);
                  }}
                >
                  Edit Contact
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      className: "whitespace-nowrap",
    },
  ];

  const filterItems: FilterItem[] = [
    {
      type: "search",
      title: "Client Name",
      value: name,
      onValueChange: setName,
    },
  ];

  return (
    <MainLayout
      breadcrumb={[{ name: "Vendors", href: "#" }]}
      pageHeader={{
        title: "Clients",
        subtitle: "Manage your clients and their contacts.",
        buttonText: "+ Invite Client",
        onButtonClick() {
          setInviteModalOpen(true);
        },
      }}
    >
      <SimpleTable<Customer>
        columns={columns}
        data={list}
        totalCount={total}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        sortKey={sortKey}
        sortOrder={sortOrder}
        onSortChange={(key) => setSortKey(key as CustomerSortKey)}
        filters={filterItems}
        onReset={() => {
          setName("");
        }}
      />

      <EditClientContact
        open={editingModalOpen}
        onOpenChange={setEditingModalOpen}
        onSubmit={onSubmit}
        clientToEdit={clientToEdit}
      />

      <InviteClientModal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
        onSubmit={handleInviteClient}
        saving={inviteSaving}
        error={inviteError}
      />
    </MainLayout>
  );
}
